import { Link } from "react-router-dom";
import { ArrowRight, Activity, ClipboardList, GitBranch, HardHat, Wallet, FileDown } from "lucide-react";
import { Logo, TAGLINE, SUB_TAGLINE, ABOUT_TEXT } from "@/components/Logo";
import { Badge2 } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import BackToTop from "@/components/BackToTop";
import TrustBar from "@/components/TrustBar";
import PublicHeader from "@/components/PublicHeader";
import { useMeta } from "@/lib/useMeta";
import { COMPANY } from "@/lib/company";

const PILLARS = [
  { icon: Activity, title: "S-Curve Tracking", text: "Planned, actual and forecast progress overlaid in real time from validated field data." },
  { icon: ClipboardList, title: "Daily Progress Reports", text: "Shift-wise DPR with manpower, idle time, weather and digital sign-off — balance quantities recompute instantly." },
  { icon: GitBranch, title: "CPM & Delay Control", text: "Live critical path with total and free float, plus a hindrance matrix that prices every delay day." },
  { icon: HardHat, title: "Resource & Workforce", text: "Machinery matrix, trade-wise manpower density against productivity norms and daily reconciliation." },
  { icon: Wallet, title: "EVM Costing", text: "CPI, SPI, EAC and VAC bound to physical progress in MT, Cu.M and Dia-Inch — no hidden overruns." },
  { icon: FileDown, title: "One-Click Reporting", text: "Board-ready PPTX, Excel data packs, A4/A3 print PDFs and a live Power BI feed." },
];

const SECTORS = ["Thermal Power Plants", "T&D / Substations", "Solar Parks", "Refinery & Petrochemical", "Nuclear Facilities", "Heavy Infrastructure"];

export default function About() {
  useMeta({
    title: "About Plan-Sarthi — EPC Project Controls by Sarthi Associates",
    description:
      "Plan-Sarthi is built by Sarthi Associates for India's EPC industry — power, refinery, nuclear and infrastructure execution. Our story, mission and the team behind the platform.",
    path: "/about",
  });

  return (
    <div className="min-h-screen" data-testid="about-page">
      <PublicHeader />
      <header className="h-14 border-b border-border flex items-center gap-3 px-4 sm:px-6 sticky top-14 md:top-16 z-20 bg-background/85 backdrop-blur-md">
        <span className="text-[12px] uppercase tracking-[0.16em] text-muted-foreground">About us</span>
        <div className="ml-auto flex items-center gap-2">
          <Link to="/login"><Button size="sm" data-testid="about-signin-btn">Sign in <ArrowRight className="w-3.5 h-3.5 ml-1.5" /></Button></Link>
        </div>
      </header>

      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 grid-noise opacity-[0.35] dark:opacity-[0.18]" />
        <div className="relative max-w-[1100px] mx-auto px-5 sm:px-8 py-14 sm:py-20">
          <span className="inline-block border border-[#F97316]/50 text-[#F97316] text-[10px] uppercase tracking-[0.18em] px-2 py-1" data-testid="about-subtagline">
            {SUB_TAGLINE}
          </span>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.08] mt-5 max-w-3xl" data-testid="about-tagline">
            Plan Smarter. Build Stronger. <span className="text-[#F97316]">Deliver on Time.</span>
          </h1>
          <p className="text-base md:text-lg text-muted-foreground mt-4 max-w-2xl">
            One digital workspace for EPC planning, field execution and financial control.
          </p>
          <div className="flex flex-wrap gap-2 mt-6">
            {SECTORS.map((s) => <Badge2 key={s} tone="primary">{s}</Badge2>)}
          </div>
          <Link to="/login" className="inline-block mt-8">
            <Button size="lg" data-testid="about-cta-btn">Enter the workspace <ArrowRight className="w-4 h-4 ml-1.5" /></Button>
          </Link>
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-5 sm:px-8 py-12">
        <h2 className="text-base md:text-lg font-semibold uppercase tracking-wider text-muted-foreground">About Plan-Sarthi</h2>
        <p className="text-sm sm:text-base leading-relaxed mt-4 max-w-4xl" data-testid="about-description">
          {ABOUT_TEXT}
        </p>
      </section>

      <section className="max-w-[1100px] mx-auto px-5 sm:px-8 pb-14">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {PILLARS.map((p) => (
            <div key={p.title} className="border border-border bg-card p-4 rise" data-testid={`about-pillar-${p.title.replace(/\s|&|\//g, "-").toLowerCase()}`}>
              <p.icon className="w-5 h-5 text-[#F97316]" />
              <h3 className="font-semibold mt-2.5">{p.title}</h3>
              <p className="text-[12px] text-muted-foreground mt-1.5 leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-border">
        <section id="team" className="max-w-[1100px] mx-auto px-5 sm:px-8 py-14 scroll-mt-32" data-testid="about-team">
          <span className="text-[11px] uppercase tracking-[0.18em] text-[#F97316]">Our team</span>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold mt-3">Engineers who have run the packages</h2>
          <p className="text-sm text-muted-foreground mt-4 max-w-[720px] leading-relaxed">
            Plan-Sarthi is built by {COMPANY.legalName} — a small team combining site execution experience on heavy
            industrial packages with software engineering. Every module started as a spreadsheet someone maintained at
            2 AM before a client review.
          </p>
          <div className="grid sm:grid-cols-3 gap-3 mt-7">
            {[
              ["Project controls", "Planning, EVM and claims experience from thermal, nuclear and infrastructure packages."],
              ["Product & engineering", "Offline-first field software built for 2G connections and 6 AM shift changes."],
              ["Implementation", "WBS coding, BOQ import, baselines and role setup for your first package."],
            ].map(([title, text]) => (
              <div key={title} className="border border-border bg-card p-5" data-testid={`team-card-${title.replace(/\s|&/g, "-").toLowerCase()}`}>
                <h3 className="font-semibold text-[14px] text-[#F97316]">{title}</h3>
                <p className="text-[12.5px] text-muted-foreground mt-2 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
          <p className="text-[12px] text-muted-foreground mt-5">
            Want to join?{" "}
            <Link to="/careers" className="text-[#F97316] hover:underline" data-testid="about-careers-link">See open roles</Link>{" "}
            or read the{" "}
            <Link to="/blog" className="text-[#F97316] hover:underline" data-testid="about-blog-link">blog</Link>.
          </p>
        </section>
      </footer>
      <footer className="border-t border-border">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <Logo size="sm" />
          <div className="sm:ml-auto text-[11px] text-muted-foreground sm:text-right leading-relaxed">
            <div>{TAGLINE}</div>
          </div>
        </div>
      </footer>
      <TrustBar />      <Footer />
      <BackToTop />
    </div>
  );
}
